import ConsoleTable from '../../components/ConsoleTable.jsx';
import { licenseRequirement } from '../../../../shared/collectors.mjs';
import { localDayKey } from '../../lib/score-history.js';
import {
  collectionTone, collectionWord, durationText, partialSources, retryText, runRows, skipReasonText, sourceLabel,
  sourceNote, sourcesLine, triggerText,
} from '../../lib/collection-status.js';
import { formatScore } from '../../lib/score-format.js';

// History (Data collection, mockup 3): the collections on ConsoleTable, newest first. Columns When, Status, Trigger,
// Sources, Rows, Duration. The Sources cell names partial sources, so a partial run is visible without opening the row;
// the row expands to every source with its rows, error or skip reason. Status is a dot plus an ink word.

const DOT = Object.freeze({ good: 'var(--good)', warn: 'var(--warn-dot)', bad: 'var(--bad)', off: 'var(--track)' });

function Dot({ tone }) {
  return <span aria-hidden="true" className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: DOT[tone] }} />;
}

const timeOf = (t) => new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

/** "Today", "Yesterday" or "Sun 19 Oct", and the time, for a run timestamp. */
export function whenParts(ts, now = Date.now()) {
  const t = Date.parse(ts ?? '');
  if (!Number.isFinite(t)) return { day: '--', time: '' };
  const day = localDayKey(t);
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  let word;
  if (day === localDayKey(now)) word = 'Today';
  else if (day === localDayKey(y)) word = 'Yesterday';
  else word = new Date(t).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '').replace('Sept', 'Sep');
  return { day: word, time: timeOf(t) };
}

function StatusCell({ run }) {
  const retry = retryText(run);
  return (
    <div>
      <span className="inline-flex items-center gap-2 whitespace-nowrap font-semibold text-ipc-ink" data-run-status={run.statusCode}>
        <Dot tone={collectionTone(run.statusCode)} />
        {collectionWord(run.statusCode)}
      </span>
      {retry ? <div className="text-[12.5px] text-ipc-muted">{retry}</div> : null}
    </div>
  );
}

/** Every source of one collection: rows, the error (with the license or catalog note) or why it was skipped. */
export function RunDetail({ run }) {
  const failedMap = {};
  const failedLicense = {};
  for (const f of run.failed ?? []) { failedMap[f.name] = f.error; if (f.license) failedLicense[f.name] = true; }
  const skipReasons = run.skipReasons ?? {};
  const skipped = new Set(run.skipped ?? []);
  const partial = new Set(partialSources(run));
  const rowCounts = run.rowCounts ?? {};
  const all = [...(run.succeeded ?? []), ...(run.failed ?? []).map((f) => f.name), ...(run.skipped ?? [])]
    .sort((a, b) => sourceLabel(a).localeCompare(sourceLabel(b)));
  const facts = [
    ['Rows', runRows(run).toLocaleString('en-US')],
    ['Duration', durationText(run.durationMs) ?? '--'],
    ['Trigger', triggerText(run)],
    ['Data date', run.auditDataDate ?? run.collectionDate ?? '--'],
  ];
  if (run.securityPosture?.score != null) facts.push(['onGuard score', formatScore(run.securityPosture.score)]);
  return (
    <div className="space-y-3 py-1" data-testid="run-detail">
      <dl className="flex flex-wrap gap-x-8 gap-y-1 text-sm">
        {facts.map(([k, v]) => (
          <div key={k}><dt className="text-[12.5px] text-ipc-muted">{k}</dt><dd className="font-semibold text-ipc-ink">{v}</dd></div>
        ))}
      </dl>
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-[0.04em] text-ipc-muted">Sources</h4>
        <ul className="mt-1">
          {all.map((id) => {
            const isFailed = id in failedMap;
            const isSkipped = skipped.has(id);
            const isPartial = partial.has(id);
            const tone = isFailed ? 'bad' : isSkipped ? 'off' : isPartial ? 'warn' : 'good';
            const rows = rowCounts[id];
            let note = null;
            if (isFailed) {
              const extra = failedLicense[id] && licenseRequirement(id) ? `: ${licenseRequirement(id)}, turned off` : (!failedLicense[id] && sourceNote(id) ? `: ${sourceNote(id)}` : '');
              note = `Failed: ${failedMap[id] ?? 'unknown error'}${extra}`;
            } else if (isSkipped) {
              note = skipReasonText(id, skipReasons[id]);
            } else if (isPartial) {
              note = 'Partial data';
            }
            return (
              <li key={id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-t border-ipc-border py-1.5 text-sm first:border-t-0" data-source={id}>
                <Dot tone={tone} />
                <span className="min-w-0 flex-1 text-ipc-ink">{sourceLabel(id)}</span>
                {note ? <span className="text-[13px] text-ipc-muted [overflow-wrap:anywhere]">{note}</span> : null}
                {rows !== undefined ? <span className="font-mono text-[12.5px] text-ipc-muted">{rows.toLocaleString('en-US')} rows</span> : null}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/**
 * @param {{ runs: object[], loading?: boolean, error?: string|null, onRetry?: () => void, hasMore?: boolean,
 *   onLoadMore?: () => void, loadingMore?: boolean }} props
 * Ten rows a page; the pager reaches every loaded collection and Load more fetches older ones (13 months are kept).
 */
export default function HistoryTable({ runs, loading = false, error = null, onRetry, hasMore = false, onLoadMore, loadingMore = false }) {
  const columns = [
    {
      id: 'when',
      header: 'When',
      cell: (r) => {
        const w = whenParts(r.runTimestamp);
        return <span className="sm:whitespace-nowrap"><span className="whitespace-nowrap font-semibold text-ipc-ink">{w.day}</span>{w.time ? `, ${w.time}` : ''}</span>;
      },
    },
    { id: 'status', header: 'Status', cell: (r) => <StatusCell run={r} /> },
    { id: 'trigger', header: 'Trigger', hideBelow: 'md', cell: (r) => triggerText(r) },
    { id: 'sources', header: 'Sources', hideBelow: 'sm', cell: (r) => <span className="text-ipc-ink" data-sources-line="">{sourcesLine(r)}</span> },
    { id: 'rows', header: 'Rows', align: 'right', hideBelow: 'md', cell: (r) => <span className="tabular-nums">{runRows(r).toLocaleString('en-US')}</span> },
    { id: 'duration', header: 'Duration', align: 'right', hideBelow: 'lg', cell: (r) => <span className="whitespace-nowrap tabular-nums">{durationText(r.durationMs) ?? '--'}</span> },
  ];
  return (
    <ConsoleTable
      label="Collection history"
      columns={columns}
      rows={runs}
      getRowId={(r) => String(r.runTimestamp)}
      rowName={(r) => { const w = whenParts(r.runTimestamp); return `${w.day} ${w.time}`.trim(); }}
      renderExpanded={(r) => <RunDetail run={r} />}
      pageSizes={[10, 25, 50, 100]}
      loading={loading}
      error={error}
      onRetry={onRetry}
      emptyState={{ title: 'No collection yet', body: 'The history fills after the first collection.' }}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      loadingMore={loadingMore}
      framed={false}
    />
  );
}
